<template>
    <vx-popover hover arrow placement="top">
        <slot>
            <button
                type="button"
                class="inline-flex cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                aria-label="What each group role can do"
            >
                <font-awesome-icon widthAuto class="text-slate-500" :icon="faCircleQuestion" />
            </button>
        </slot>

        <template v-slot:content>
            <div class="max-w-[calc(100vw-4rem)] overflow-x-auto">
                <table class="text-xs text-slate-700 border-collapse">
                    <thead>
                        <tr>
                            <th scope="col" class="pb-2 pr-4 text-left font-bold text-slate-800">
                                What a Group user gets
                            </th>
                            <th
                                v-for="role in ROLES"
                                :key="role"
                                scope="col"
                                class="pb-2 px-2 text-left font-bold text-slate-800"
                                :data-test="`group-role-column-${role}`"
                            >
                                {{ GROUP_USER_ROLES[role] }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="permission in PERMISSIONS"
                            :key="permission.key"
                            class="border-0 border-t border-solid border-slate-100"
                        >
                            <th scope="row" class="py-1.5 pr-4 text-left font-normal align-top">
                                <div class="whitespace-nowrap">{{ permission.label }}</div>
                                <div
                                    v-if="permission.caveat"
                                    class="text-[11px] leading-tight text-slate-500"
                                    :data-test="`group-role-caveat-${permission.key}`"
                                >
                                    {{ permission.caveat }}
                                </div>
                            </th>
                            <td
                                v-for="role in ROLES"
                                :key="role"
                                class="py-1.5 px-2 whitespace-nowrap align-top"
                                :class="accessOf(permission, role) === 'no' ? 'text-slate-400' : 'text-slate-800'"
                                :data-test="`group-role-${permission.key}-${role}`"
                            >
                                {{ ACCESS_LABELS[accessOf(permission, role)] }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </vx-popover>
</template>

<script setup lang="ts">
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxPopover } from '@voxie/frontend-components';
import { CAMPAIGN_ROLES, GROUP_USER_ROLES, SEGMENT_EDITOR_ROLES, type GroupRole } from '~/constants/roles';

type Access = 'yes' | 'no' | 'read_only';

interface Permission {
    key: string;
    label: string;
    granted: readonly GroupRole[];
    readOnly?: readonly GroupRole[];
    caveat?: string;
}

const ROLES: readonly GroupRole[] = ['owner', 'admin', 'marketer', 'blast', 'agent'];

const ACCESS_LABELS: Record<Access, string> = {
    yes: 'Yes',
    no: 'No',
    read_only: 'Read-only',
};

const PERMISSIONS: readonly Permission[] = [
    {
        key: 'message-hub',
        label: 'Message Hub',
        granted: ROLES,
        caveat: "Only when groups don't use the corporate number.",
    },
    { key: 'quick-blast', label: 'Quick Blast and Campaigns', granted: CAMPAIGN_ROLES },
    { key: 'analytics', label: 'Analytics', granted: ROLES.filter((role) => 'agent' !== role) },
    { key: 'segments-list', label: 'Segments list', granted: SEGMENT_EDITOR_ROLES, readOnly: CAMPAIGN_ROLES },
    { key: 'segments-edit', label: 'Create or edit segments', granted: SEGMENT_EDITOR_ROLES },
    { key: 'group-management', label: 'Imports and Group users', granted: ['owner', 'admin'] },
    { key: 'terms', label: 'Group Terms & Conditions', granted: ['owner'] },
    {
        key: 'payment-method',
        label: 'Payment method',
        granted: ['owner', 'admin'],
        caveat: 'Only for groups billed on their own.',
    },
];

const accessOf = (permission: Permission, role: GroupRole): Access => {
    if (permission.granted.includes(role)) {
        return 'yes';
    }

    return permission.readOnly?.includes(role) ? 'read_only' : 'no';
};
</script>
